import { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useAssignSims, useSimDevices } from "@/hooks/useSims";
import type { SimCard } from "@/lib/sims";

interface AssignSimsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sims: SimCard[];
  allSims: SimCard[];
  defaultInstanceId?: string | null;
  onDone?: () => void;
}

export const AssignSimsDialog = memo(function AssignSimsDialog({
  open,
  onOpenChange,
  sims,
  allSims,
  defaultInstanceId,
  onDone,
}: AssignSimsDialogProps) {
  const { data: devices, tagIds, isLoading } = useSimDevices();
  const assign = useAssignSims();
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [slot, setSlot] = useState("");

  useEffect(() => {
    if (open) {
      setInstanceId(defaultInstanceId ?? null);
      setSlot("");
    }
  }, [open, defaultInstanceId]);

  const selectedIds = useMemo(() => new Set(sims.map((s) => s.id)), [sims]);
  const takenSlots = useMemo(
    () =>
      allSims
        .filter(
          (s) =>
            s.equipmentInstanceID === instanceId &&
            s.slot !== null &&
            !selectedIds.has(s.id),
        )
        .map((s) => s.slot as number)
        .sort((a, b) => a - b),
    [allSims, instanceId, selectedIds],
  );

  const slotNumber = slot.trim() ? Number(slot) : null;
  const slotInvalid =
    slotNumber !== null && (!Number.isInteger(slotNumber) || slotNumber < 1);
  const wantedSlots =
    slotNumber && !slotInvalid
      ? sims.map((_, index) => slotNumber + index)
      : [];
  const conflict = wantedSlots.filter((s) => takenSlots.includes(s));

  const submit = () => {
    if (!instanceId) return;
    assign.mutate(
      {
        simIds: sims.map((s) => s.id),
        equipmentInstanceId: instanceId,
        slot: slotNumber,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-sims-dialog">
        <DialogHeader>
          <DialogTitle>
            Assign {sims.length} SIM{sims.length === 1 ? "" : "s"} to a device
          </DialogTitle>
          <DialogDescription>
            The device name and slot are also written to the SIM label in
            Simetric.
          </DialogDescription>
        </DialogHeader>
        {tagIds.length === 0 && !isLoading ? (
          <p className="text-sm text-muted-foreground">
            No SIM device tags configured yet. Choose which equipment can hold
            SIMs in{" "}
            <Link to="/admin/settings" className="underline">
              Settings
            </Link>
            .
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Device</Label>
              <EntityCombobox
                testId="assign-sims-device"
                options={devices.map((d) => ({ value: d.id, label: d.name }))}
                value={instanceId}
                onChange={setInstanceId}
                placeholder={isLoading ? "Loading devices…" : "Select device…"}
                searchPlaceholder="Search devices…"
                emptyMessage="No matching device"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-sims-slot">
                {sims.length > 1 ? "First slot (optional)" : "Slot (optional)"}
              </Label>
              <Input
                id="assign-sims-slot"
                data-testid="assign-sims-slot"
                inputMode="numeric"
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                placeholder="e.g. 1"
                className="w-32"
              />
              {sims.length > 1 && slotNumber && !slotInvalid && (
                <p className="text-xs text-muted-foreground">
                  SIMs get slots {slotNumber}–{slotNumber + sims.length - 1} in
                  selection order.
                </p>
              )}
              {instanceId && (
                <p className="text-xs text-muted-foreground">
                  Slots taken on this device:{" "}
                  {takenSlots.length > 0 ? takenSlots.join(", ") : "none"}
                </p>
              )}
              {slotInvalid && (
                <p className="text-xs text-destructive">
                  Slot must be a positive whole number.
                </p>
              )}
              {conflict.length > 0 && (
                <p className="text-xs text-destructive">
                  Slot {conflict.join(", ")} already taken.
                </p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-assign-sims"
            onClick={submit}
            disabled={
              !instanceId ||
              slotInvalid ||
              conflict.length > 0 ||
              assign.isPending
            }
          >
            {assign.isPending ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
